import { CdkMenu, CdkMenuGroup, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { FastSvgComponent } from '@push-based/ngx-fast-svg';
import { DropdownEnterLeave, InitialsPipe, MenuItem, WhAppLink } from '@wheere-front/shared-util';
import { LanguageService } from './services/language.service';
import { preferenceEntries } from './services/preferences-menu';
import { ThemeService } from './services/theme.service';

@Component({
  selector: 'lib-avatar-menu',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    CdkMenuGroup,
    CdkMenu,
    CdkMenuItem,
    CdkMenuTrigger,
    InitialsPipe,
    FastSvgComponent,
    TranslocoPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [DropdownEnterLeave],
  template: `
    <button
      [cdkMenuTriggerFor]="menu"
      [cdkMenuPosition]="menuPosition"
      #cdkMenuTriggerFor="cdkMenuTriggerFor"
      type="button"
      class="focus:ring-primary-100 flex items-center justify-center gap-1.5 rounded-full border border-gray-200 bg-white p-1 shadow transition-colors ease-in hover:shadow-md active:shadow-sm active:outline-none lg:p-0 lg:focus:ring-4 dark:border-slate-600 dark:bg-slate-800">
      <span class="sr-only">Account menu</span>
      @if (menuItems()?.length) {
        <div class="pl-1 lg:hidden">
          <i class="pi pi-bars text-gray-600"></i>
        </div>
      }
      <div class="bg-primary-500 relative inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full">
        <span class="font-medium uppercase text-white">{{ fullname() | initials }}</span>
      </div>
    </button>

    <ng-template #menu>
      @defer (when cdkMenuTriggerFor.isOpen()) {
        <div
          cdkMenu
          @DropdownEnterLeave
          class="mt-2 w-56 divide-y divide-gray-100 rounded-xl border border-gray-100 bg-white shadow-lg dark:divide-slate-700 dark:border-slate-700 dark:bg-slate-800">
          <!-- User info -->
          <div class="px-4 py-3 text-sm text-gray-900 dark:text-slate-100">
            <div class="truncate font-semibold">{{ fullname() }}</div>
            <div class="truncate text-gray-500 dark:text-slate-400">{{ email() }}</div>
          </div>

          <!-- Navigation items (mobile only) -->
          @if (menuItems()?.length) {
            <div cdkMenuGroup class="py-1 lg:hidden">
              @for (item of menuItems(); track $index) {
                <a
                  cdkMenuItem
                  [routerLink]="'./' + item.path"
                  routerLinkActive="bg-gray-50 font-semibold text-primary-500"
                  [routerLinkActiveOptions]="{ exact: true }"
                  class="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
                  @if (item.icon) {
                    <div class="flex h-4 w-4 shrink-0 items-center justify-center text-gray-400">
                      <fast-svg [name]="item.icon" width="100%" height="100%" />
                    </div>
                  }
                  {{ item.label }}
                </a>
              }
            </div>
          }

          <!-- Application : bascule vers les autres apps Wheere permises (Inside / Integrator / Customer). -->
          @if (apps().length) {
            <div cdkMenuGroup class="py-1" data-testid="avatar-apps">
              <div
                class="px-4 pb-1 pt-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
                {{ 'nav.application' | transloco }}
              </div>
              @for (app of apps(); track app.id) {
                <a
                  cdkMenuItem
                  [href]="app.href"
                  [attr.aria-current]="app.current ? 'true' : null"
                  class="flex items-center justify-between gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  [class.font-semibold]="app.current"
                  >{{ app.label }}
                  @if (app.current) {
                    <i class="pi pi-check text-primary-500 text-xs" aria-hidden="true"></i>
                  } @else {
                    <i class="pi pi-external-link text-xs text-gray-400 dark:text-slate-500" aria-hidden="true"></i>
                  }
                </a>
              }
            </div>
          }

          <!-- Préférences : langue et thème (sortis de la barre du haut). Un clic fait tourner la
               valeur ; le menu se referme et l'interface change sous les yeux. cdkMenuItemTriggered
               (et non (click)) : il part avant que le CDK ne referme le menu et détruise la vue. -->
          @if (withPreferences()) {
            <div cdkMenuGroup class="py-1" data-testid="avatar-preferences">
              @for (p of preferences(); track p.id) {
                <button
                  cdkMenuItem
                  type="button"
                  [attr.data-testid]="'avatar-pref-' + p.id"
                  (cdkMenuItemTriggered)="p.run()"
                  class="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
                  <i class="text-sm text-gray-400 dark:text-slate-500" [class]="p.icon"></i>
                  <span class="flex-1">{{ p.labelKey | transloco }}</span>
                  <span class="text-xs font-semibold text-gray-500 dark:text-slate-400">{{
                    p.value ?? (p.valueKey ?? '' | transloco)
                  }}</span>
                </button>
              }
            </div>
          }

          <!-- Account + Logout -->
          <div cdkMenuGroup class="py-1">
            @if (withAccount()) {
              <a
                cdkMenuItem
                routerLink="/account"
                routerLinkActive="font-semibold"
                class="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
                <i class="pi pi-user text-sm text-gray-400"></i>
                Account
              </a>
            }
            <button
              cdkMenuItem
              (click)="logout.emit()"
              class="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
              <i class="pi pi-sign-out text-sm text-gray-400"></i>
              Sign out
            </button>
          </div>
        </div>
      }
    </ng-template>
  `,
})
export class AvatarMenuComponent {
  fullname = input.required<string>();
  email = input.required<string | undefined>();
  withAccount = input(true);
  menuItems = input<MenuItem[]>();
  /** Affiche Langue et Thème dans le menu (la barre du haut ne les porte plus). */
  withPreferences = input(false);
  /** Choix « Application » (vide = section masquée). */
  apps = input<WhAppLink[]>([]);
  logout = output<void>();

  readonly #lang = inject(LanguageService);
  readonly #theme = inject(ThemeService);
  protected readonly preferences = computed(() => preferenceEntries(this.#lang, this.#theme));

  readonly menuPosition: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
  ];
}
